import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { LoadingButton } from '@mui/lab';
import { useTheme } from '@mui/material/styles';
import {
  Stack,
  Dialog,
  Button,
  TextField,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';

import { usePermission } from 'src/hooks/usePermission';

import { updateRole } from '../../../services/role.service';

export default function RoleEditDialog({ open, role, onClose, onSuccess }) {
  const { t } = useTranslation();
  const { can } = usePermission();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ roleName: '', description: '' });

  const canUpdate = can({ permission: 'ROLES:UPDATE' });

  useEffect(() => {
    if (role) {
      const cleanName = role.roleName
        ? role.roleName
            .replace('ROLE_', '')
            .replace(/_/g, ' ')
            .trim()
            .toLowerCase()
            .split(' ')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')
        : '';

      setFormData({
        roleName: cleanName,
        description: role.description || '',
      });
    }
  }, [role]);

  /**
   * PERSISTENCE FORMATTER
   * Converts friendly input back into technical SNAKE_CASE.
   */
  const handleSave = async () => {
    if (!formData.roleName || loading || !canUpdate) return;
    setLoading(true);
    try {
      const technicalName = `ROLE_${formData.roleName
        .trim()
        .toUpperCase()
        .replace(/\s+/g, '_')}`;

      const finalData = {
        roleName: technicalName,
        description: formData.description,
      };

      await updateRole(role.id, finalData);
      onSuccess();
      onClose();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: '24px',
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: isDark ? 'rgba(22, 28, 36, 0.9)' : 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>{t('rolesPage.editInfo')}</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <TextField
            fullWidth
            label={t('rolesPage.roleName')}
            value={formData.roleName}
            disabled={!canUpdate}
            onChange={(e) => setFormData({ ...formData, roleName: e.target.value })}
            variant="outlined"
          />
          <TextField
            fullWidth
            multiline
            rows={4}
            label={t('rolesPage.description')}
            value={formData.description}
            disabled={!canUpdate} // MASKED
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            variant="outlined"
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} color="inherit" sx={{ fontWeight: 700 }}>
          {t('buttons.cancel')}
        </Button>
        <LoadingButton
          loading={loading}
          onClick={handleSave}
          variant="contained"
          disabled={!canUpdate}
          sx={{
            bgcolor: '#00A76F',
            fontWeight: 800,
            borderRadius: '12px',
            '&:hover': { bgcolor: '#008F5D' },
          }}
        >
          {t('buttons.update')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

RoleEditDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  role: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};
